import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";
import { Caret, Drawer, Pill } from "@picklebet/ui";

import { scrollIntoViewIfOffscreen } from "@/utilities/dom";
import * as styles from "./ExpandableOutcomes.module.scss";

const NAV_OFFSET = 150;

type ExpandableOutcomesProps = {
  hiddenCount: number;
  collapsed: React.ReactNode;
  className?: string;
  collapsedClassName?: string;
  children: React.ReactNode;
  // key for the active search/type filter, null when none active. Expanded
  // on mount if one's active, and re-expands on each new value, but the
  // viewer can still collapse it again after.
  forceExpand?: string | null;
};

const ExpandableOutcomes = ({
  hiddenCount,
  collapsed,
  className,
  collapsedClassName,
  children,
  forceExpand,
}: ExpandableOutcomesProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(!!forceExpand);
  const prevForceExpand = useRef(forceExpand);

  // the toggle is gated on hiddenCount > 0 below, so a stale `expanded`
  // while hiddenCount is 0 just renders nothing - no need to reset it,
  // and resetting would lose the viewer's choice once hidden rows return
  useEffect(() => {
    if (forceExpand && forceExpand !== prevForceExpand.current) {
      setExpanded(true);
    }
    prevForceExpand.current = forceExpand;
  }, [forceExpand]);

  return (
    <div className={className} ref={containerRef}>
      {children}

      {hiddenCount > 0 && (
        <>
          <Drawer
            isOpen={expanded}
            className={cx(styles.collapsed, collapsedClassName)}
          >
            {collapsed}
          </Drawer>

          <div className={styles.toggle}>
            <Pill
              className={styles.showMorePill}
              onPressedChange={() => {
                if (expanded) {
                  scrollIntoViewIfOffscreen(containerRef.current, NAV_OFFSET);
                }
                setExpanded(!expanded);
              }}
              pressed={expanded}
            >
              {expanded ? "Show less" : `Show ${hiddenCount} more`}
              <Caret
                variant={expanded ? "up" : "down"}
                className={styles.caret}
              />
            </Pill>
          </div>
        </>
      )}
    </div>
  );
};

export { ExpandableOutcomes };
